<!-- Drag-over overlay: covers the whole panel, matching the real drop target -->
@if ($isDragOver()) {
  <div class="drop-overlay">
    <div class="drop-badge">
      <i class="fa-regular fa-file-arrow-up"></i>
    </div>
    <span>Drop files here</span>
  </div>
}

<!-- Main content area: welcome OR message list -->
<div class="chat-body">
  @if ($showWelcome()) {
    <div class="welcome-header">
      <h1 class="welcome-title">{{ welcomeTitle }}</h1>
    </div>
  } @else {
    <agent-message-list [messages]="$messages()" [isStreaming]="$isStreaming()" [lastMessageIsUser]="$lastMessageIsUser()" [revealedTextParts]="$revealedTextParts()" [cardActions]="$cardActions()" [blobUrls]="$blobUrls()" [collapsedActivityBlocks]="$collapsedActivityBlocks()" [expandedTools]="$expandedTools()" [streamingError]="$streamingError()" [threadLoadError]="$threadLoadError()" (cardActionComplete)="onCardActionComplete($event)" (activityBlockToggled)="onActivityBlockToggled($event)" (toolExpandToggled)="onToolExpandToggled($event)" (retry)="onRetry()" />
  }
</div>

<!-- Suggest actions (both modes, above the input) -->
@if ($showWelcome()) {
  <agent-suggest-actions [config]="$suggestConfig()" (promptSelected)="onSuggestPrompt($event)" />
}

<!-- Composer stack: flows inline normally; floats over the full-bleed scroller in fullscreen chat -->
<div class="composer-overlay" #composerOverlay [class.has-feedback-prompt]="$showFeedbackPrompt()">
  <!-- Card overlays replace the input bar when active. Only ONE card is ever pending at a time
       (interview OR write-tool approval, never both), so this is a simple mutually-exclusive swap. -->
  @if ($threadUnavailable()) {
    <!-- Missing or inaccessible saved conversation: the notice takes the composer's place, ranked
         ABOVE the cards because nothing can be sent or answered here, including a pending interview
         or approval. A transient load failure never lands here; it stays an alert in the transcript. -->
    <wb-guard-notice band="blocked" title="Conversation unavailable" body="It may have been deleted, or you don't have access to it." actionLabel="Start a new chat" (action)="onContextGuardNewChat()" />
  } @else if ($activeQuestionCard(); as qCard) {
    <agent-question-card [data]="qCard.data" [disabled]="!!$cardActions()[qCard.callId]" (submit)="onQuestionSubmit({ toolCallId: qCard.callId, action: $event.action, answers: $event.answers })" (cancel)="onCardActionComplete({ toolCallId: qCard.callId, action: 'skipped' })" />
  } @else if ($activePendingApproval(); as appr) {
    <!-- Write-tool approval card (overlays the composer like the question card) -->
    <agent-confirmation-card [title]="appr.displayName" [fields]="appr.fields" [context]="appr.context" [reasoning]="appr.reasoning" [disabled]="false" (approve)="onApproveToolCall(appr.callId)" (decline)="onDeclineToolCall({ id: appr.callId, reason: 'Declined by user' })" />
  } @else if ($contextGuardBlocked()) {
    <!-- Conversation-length limit reached: the notice takes the composer's place and borrows its
         footprint, so sending and uploading are structurally impossible rather than merely disabled
         and the composer area does not shift. Ranked BELOW the cards above so an already-running
         turn can always be finished before this settled state appears. -->
    <agent-context-guard-notice [band]="'blocked'" (newChat)="onContextGuardNewChat()" />
  } @else if ($usageLimitBlocked()) {
    <!-- Workspace AI spend cutoff (assistant stops at 90%, everything at 100%): the notice takes
         the composer's place with the server-built copy, so sending is structurally impossible —
         matching the API, which refuses the request anyway. -->
    <ai-usage-limit-notice [status]="$usageLimitStatus()" [assistant]="true" />
  } @else if ($providerUnavailable()) {
    <!-- The model provider itself refused (its own quota/billing/capacity — typed by the API relay,
         never our tenancy caps). Terminal treatment in the composer's place; "Try again" restores
         the composer since provider hiccups can clear at any moment. -->
    <wb-guard-notice band="blocked" [title]="providerUnavailableTitle" [body]="providerUnavailableDetail" actionLabel="Try again" (action)="onProviderUnavailableRetry()" />
  } @else {
    <!-- Conversation-length nudge (neutral advisory or amber approaching endcap). It sits above the composer and leaves it
         fully usable, including Stop while a reply is still streaming. -->
    @if ($contextGuardNoticeBand(); as guardBand) {
      <agent-context-guard-notice [band]="guardBand" (newChat)="onContextGuardNewChat()" (dismiss)="onContextGuardDismiss()" />
    }
    <!-- Workspace AI spend nudge (neutral 50% or amber 80% ONLY — blocked renders in the composer's
         place above, never here): server-built copy, dismissible until the window rolls over or the
         state escalates. -->
    @if ($usageLimitNudgeStatus(); as usageStatus) {
      <ai-usage-limit-notice [status]="usageStatus" [assistant]="true" (dismiss)="onUsageLimitDismiss()" />
    }
    <!-- Rate-this-conversation prompt, shown above the composer when the chat looks done -->
    @if ($showFeedbackPrompt() && $currentThreadId(); as threadId) {
      <agent-thread-feedback [threadId]="threadId" (handled)="onFeedbackHandled()" />
    }
    <!-- Input bar. Disabled while a saved conversation's history is still loading: until it lands,
         the thread is not yet known to exist or be accessible, so nothing may be sent into it. -->
    <agent-prompt-input [isStreaming]="$isStreaming()" [disabled]="$threadLoading()" [pendingFiles]="$pendingFiles()" [modelLocked]="$modelLocked()" [threadModelTier]="$threadModelTier()" [draft]="$composerDraft()" [conversationKey]="$draftKey()" (draftChange)="onDraftChange($event)" (send)="onSend($event)" (stop)="onStop()" (fileSelected)="onFileSelected($event)" (removeFile)="onRemoveFile($event)" (mentionSearch)="onMentionSearch($event)" />
  }

  <!-- Shortcut hints (shared) -->
  @if ($activeHints().length > 0) {
    <agent-shortcut-hints [hints]="$activeHints()" />
  }
</div>

<!-- Mention dropdown -->
<agent-mention-dropdown [query]="$mentionQuery()" [results]="$mentionResults()" [isVisible]="$mentionVisible()" [position]="$mentionPosition()" (selected)="onMentionSelected($event)" (dismissed)="onMentionDismissed()" />
